import React, { useEffect, useState } from "react";
import {Button, Table} from "reactstrap";
import TagSearchingInput from "src/app/shared/components/tag-searching-input/TagSearchingInput";
import { getContactSearchingTags } from "./../../~store/actions/constants";
import {IApplicationState} from "src/~store/models/ApplicationState";
import {IContactListProps} from "./~types/ContactListProps";
import Loader from "src/app/shared/components/loader/Loader";
import ContactItem from "./contact-item/ContactItem";
import { 
    REQUESTED_NUMBER_OF_CONTACTS, 
    REQUESTED_NUMBER_OF_CONTACTS_FIRST_TIME,
    TRIGGER_SCROLL_HEIGHT
} from "./ContactListConstants";
import {connect} from "react-redux";

import {
    contactsSelector, 
    suggestionContactsSelector,
    isReceivingContactsSelector, 
    isContactCreatingSelector,
    countOfContactsSelector,
    searchStringSelector,
    tagsSelector,
    isSortingByContactLastNameSelector,
    isChatVisibleSelector,
} from "../../~store/selector";

import {
    getContactsByTags,
    getContacts,
    createContact,
    toggleContactSorting,
    setContactsTags
} from "src/app/contact/~store/actions/actions";

import "./ContactList.css";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { ISearchingTag } from "src/app/shared/components/tag-searching-input/~types/models/SearchingTag";

type ReduxType = ReturnType<typeof mapStateToProps> & IContactListProps;

const ContactList = (props: ReduxType) => {
    const { t } = useTranslation();

    const [isReceiving, setIsReceiving] = useState<boolean>(false);
    //const [currentTags, setCurrentTags] = useState<ISearchingTag[]>([]);

    useEffect(() => {
        props.getContactsByTags(0, REQUESTED_NUMBER_OF_CONTACTS_FIRST_TIME, props.contactsTags, props.isSortingByContactLastName, false);
    }, [props.contactsTags]);

    const navigate = useNavigate();

    const {isReceivingContacts} = props;

    const _getContactsTable = () => {
        return (
            <React.Fragment>
                {(props.contacts.length > 0) && <Table className="contact-list-table card-table">
                    {_getContactsTableHead()}
                    {_mapContactsToTableBody()}
                </Table>}
                {props.contacts.length === 0 && !props.isReceivingContacts &&
                    <div className="contact-list__no-contacts-text">
                        {t('contact.noContacts')}
                    </div>}
            </React.Fragment>
        );
    }

    const contactSortingHandler = () => {
        if(props.isReceivingContacts) return;

        props.getContactsByTags(0, REQUESTED_NUMBER_OF_CONTACTS, props.contactsTags, !props.isSortingByContactLastName, false);
        props.toggleContactSorting();
    }

    const _getContactsTableHead = () => {
        return (
            <thead>
                <tr>
                    <th>
                        <span className="channel-list__name" onClick={() => contactSortingHandler()}>{t('common.contact')}</span>
                        <span>{props.isSortingByContactLastName ? "⯆" : "⯈"}</span>
                    </th>
                    <th className="channel-list__messenger-header">{t('common.messengers')}</th>
                    <th>{t('common.created')}</th>
                    <th>{t('common.chats')}</th>
                </tr>
            </thead>
        );
    }

    const _mapContactsToTableBody = () => {
        const {contacts} = props;
        return (
            <tbody>
                {contacts.map((contact, index) => {
                    return (
                        <ContactItem 
                            contact={contact}
                            key={index} />
                    )
                })}
            </tbody>
        )
    }

    const _createContactHandler = () => {
        if (!props.isContactCreating) {
            props.createContact();
        }
        navigate("/contacts/edit-contact/0");
    }

    const _onScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        if (!isReceiving && (e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight <= TRIGGER_SCROLL_HEIGHT) && !props.isReceivingContacts) {      
            setIsReceiving(true);
            props.getContactsByTags(props.countOfContacts, REQUESTED_NUMBER_OF_CONTACTS, props.contactsTags, props.isSortingByContactLastName, false);
            setTimeout(() => {
                setIsReceiving(false);
            })
        }
    }

    const _onSearchClicked = (tags: ISearchingTag[]) => {
        if(tags.length === 0 || tags[tags.length - 1].tagType == 1) {
            props.setContactsTags(tags);
        }
    };

    return (
        <div className="contact-list-container">
                <div className="contact-list__header">
                    <Button 
                        className="contact-list__btn-create"
                        onClick={_createContactHandler}>
                                {t('contact.createContact')}
                    </Button>
                    <TagSearchingInput
                        contacts={props.suggestionContacts.length === 0 ? props.contacts : props.suggestionContacts}
                        searchingTags={getContactSearchingTags()}
                        getContacts={(searchString) => props.getContacts(0, 100, searchString, true, true, true)}
                        getContactsByTags={(searchTags) => props.getContactsByTags(0, 5, searchTags, true, true, true)}
                        onSearch={_onSearchClicked}
                        repeatingTags={false}
                        currentTags={props.contactsTags || []}
                        onClearFilter={() => props.setContactsTags([])}
                    />
                </div>
                <div 
                    onScroll={_onScroll} 
                    className={`contacts-list__body ${(props.isChatVisible ? "splittered" : "")}`}>
                    {_getContactsTable()}
                    {(isReceivingContacts && !isReceiving && <Loader />)}
                </div>
            </div>
    );
}

const mapStateToProps = (state: IApplicationState) => {
    return {
        contacts: contactsSelector(state),
        suggestionContacts: suggestionContactsSelector(state),
        isReceivingContacts: isReceivingContactsSelector(state),
        isContactCreating: isContactCreatingSelector(state),
        countOfContacts: countOfContactsSelector(state),
        searchString: searchStringSelector(state),
        contactsTags: tagsSelector(state),
        isSortingByContactLastName: isSortingByContactLastNameSelector(state),
        isChatVisible: isChatVisibleSelector(state),
    };
};

const mapDispatchToProps = (dispatch: any): IContactListProps => {
    return {
        getContactsByTags: (
            skip: number, 
            count: number,
            searchingTags: ISearchingTag[],
            isSortingByContactLastName: boolean,
            forSuggestions?: boolean
        ) => dispatch(getContactsByTags(skip, count, searchingTags, isSortingByContactLastName, false, forSuggestions)),
        getContacts: (
            skip: number, 
            count: number, 
            searchString: string | undefined, 
            isSortingByContactLastName: boolean,
            forSuggestions?: boolean
        ) => dispatch(getContacts(skip, count, searchString, isSortingByContactLastName, true, forSuggestions)),
        createContact: () => dispatch(createContact()),
        toggleContactSorting: () => dispatch(toggleContactSorting()),
        setContactsTags: (contactsTags: ISearchingTag[]) => dispatch(setContactsTags(contactsTags))
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactList);
